import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["$disabled", "$isChecked", "$size", "$variant", "theme"],
  _excluded2 = ["$disabled", "$isChecked", "$size"],
  _excluded3 = ["$isChecked"],
  _excluded4 = ["$isChecked", "$size", "theme"];
/*  eslint-disable @typescript-eslint/no-unused-vars */

import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { SuccessIcon } from 'foundations-assets';
import { Flex } from '../Flex';
import { Text } from '../Text';
import { useTrellisFocusRing } from '../../hooks/useTrellisFocusRing';
import { getSizeStyles } from './Toggle.helpers';
import { getToggleHandleCssObject, getContainerCssObject, getTextCssObject } from './Toggle.helpers';
import { AnimatePresence, m } from 'framer-motion';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const toggleSizeShorthandMap = {
  xs: 'extra-small',
  sm: 'small',
  md: 'medium'
};

// Generate toggle props descriptions:

const Wrapper = styled.div.withConfig({
  displayName: "Toggle__Wrapper",
  componentId: "zr1h4z-0"
})(["display:inline-block;", " border-radius:", ";"], props => props.$focusRingStyles, props => props.theme.borderRadius.default);
const StyledInput = styled.input.withConfig({
  displayName: "Toggle__StyledInput",
  componentId: "zr1h4z-1"
})(["opacity:0;position:absolute;"]);
const StyledTextBody = styled.div.withConfig({
  displayName: "Toggle__StyledTextBody",
  componentId: "zr1h4z-2"
})(["position:relative;width:100%;height:100%;display:flex;justify-content:center;text-wrap:nowrap;visibility:", ";top:", ";"], props => props.$visibility ? 'visible' : 'hidden', props => props.$isTop ? '9px' : '-9px');
const StyledContainer = styled(_ref => {
  let rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(m.div, Object.assign({}, rest));
})(({
  $disabled,
  $isChecked,
  $size,
  $variant,
  theme
}) => Object.assign({}, getContainerCssObject($size, $isChecked, $variant, $disabled, theme)));
const StyledHandle = styled(_ref2 => {
  let rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return /*#__PURE__*/_jsx(m.div, Object.assign({}, rest));
})(({
  $disabled,
  $isChecked,
  $size,
  theme
}) => Object.assign({}, getToggleHandleCssObject($size, $isChecked, $disabled, theme)));
const StyledCheckIcon = styled(_ref3 => {
  let rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  return /*#__PURE__*/_jsx(m.div, Object.assign({}, rest));
}).withConfig({
  displayName: "Toggle__StyledCheckIcon",
  componentId: "zr1h4z-3"
})(["height:100%;opacity:", ";"], ({
  $isChecked
}) => $isChecked ? 1 : 0);
const StyledText = styled(_ref4 => {
  let rest = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  return /*#__PURE__*/_jsx(m.div, Object.assign({}, rest));
})(({
  $isChecked,
  $size,
  theme
}) => Object.assign({}, getTextCssObject($size, $isChecked, theme)));
const spring = {
  type: 'spring',
  stiffness: 500,
  damping: 50
};
export const Toggle = ({
  checked,
  disabled = false,
  defaultChecked,
  id,
  name,
  onClick,
  onChange,
  textChecked = 'ON',
  textUnchecked = 'OFF',
  size = 'medium',
  variant = 'default'
}) => {
  const [isChecked, setIsChecked] = useState(defaultChecked || checked || false);
  useMemo(() => {
    if (checked === undefined) return;
    setIsChecked(checked);
  }, [checked]);
  const handleClick = evt => {
    if (disabled || !onChange && !onClick) {
      evt.preventDefault();
      return;
    }
    if (typeof checked !== 'boolean') {
      setIsChecked(!isChecked);
    }
    if (onChange) {
      onChange({
        target: {
          checked: !isChecked
        }
      });
    }
    if (onClick) {
      onClick({
        target: {
          checked: !isChecked
        }
      });
    }
  };
  const handleKeyDown = evt => {
    if (evt.key === ' ') {
      evt.preventDefault();
      handleClick(evt);
    }
  };
  const {
    focusProps,
    focusRingStyles
  } = useTrellisFocusRing();
  const sizeValue = toggleSizeShorthandMap[size] || size;
  const sizeStyles = getSizeStyles(sizeValue);
  return /*#__PURE__*/_jsxs(Wrapper, Object.assign({
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    $focusRingStyles: focusRingStyles,
    onClick: evt => handleClick(evt)
  }, focusProps, {
    children: [/*#__PURE__*/_jsx(StyledInput, {
      type: "checkbox",
      checked: isChecked,
      onClick: handleClick,
      onChange: () => {},
      disabled: disabled,
      id: id,
      tabIndex: -1,
      name: name
    }), /*#__PURE__*/_jsxs(StyledContainer, {
      layout: true,
      transition: spring,
      $disabled: disabled,
      $size: sizeValue,
      $isChecked: isChecked,
      $variant: variant,
      children: [/*#__PURE__*/_jsx(StyledText, {
        layout: true,
        transition: spring,
        $size: sizeValue,
        $isChecked: isChecked,
        children: /*#__PURE__*/_jsx(AnimatePresence, {
          children: /*#__PURE__*/_jsx(Text, {
            color: disabled ? 'text-primary-disabled' : isChecked || variant === 'on-dark' ? 'text-primary-on-fill-default' : 'text-primary-default',
            textAlign: "center",
            as: "h6",
            children: sizeValue === 'medium' && /*#__PURE__*/_jsxs(m.div, {
              layout: true,
              initial: {
                opacity: 0
              },
              animate: {
                opacity: 1
              },
              exit: {
                opacity: 0
              },
              transition: spring,
              children: [/*#__PURE__*/_jsx(StyledTextBody, {
                $visibility: isChecked,
                $isTop: true,
                "aria-hidden": !isChecked,
                children: textChecked
              }), /*#__PURE__*/_jsx(StyledTextBody, {
                "aria-hidden": isChecked,
                $visibility: !isChecked,
                $isTop: false,
                children: textUnchecked
              })]
            }, "text")
          })
        })
      }), /*#__PURE__*/_jsx(StyledHandle, {
        layout: true,
        transition: spring,
        $disabled: disabled,
        $isChecked: isChecked,
        $size: sizeValue,
        children: /*#__PURE__*/_jsx(StyledCheckIcon, {
          $isChecked: isChecked,
          layout: true,
          transition: spring,
          children: /*#__PURE__*/_jsx(Flex, {
            justifyContent: "center",
            alignItems: "center",
            blockSize: "100%",
            inlineSize: "100%",
            children: /*#__PURE__*/_jsx(SuccessIcon, {
              "aria-label": isChecked ? 'Success Icon' : undefined,
              size: sizeStyles.iconSize,
              color: disabled ? 'text-primary-disabled' : 'fill-secondary-default'
            })
          })
        })
      })]
    })]
  }));
};